import {
  FormControl,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 240,
    },
  },
};

const StatusFilter = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();

  const allStatusValues = ['Out of Stock', 'Low Stock', 'In Stock'];

  // Normalize value for display
  // Empty array means nothing selected (all unchecked in UI, but filter shows all items)
  // Array with all statuses means all selected (all checked in UI, filter shows all items)
  const getDisplayValue = () => {
    if (Array.isArray(value)) {
      // Empty array means nothing selected - return empty for display
      return value;
    }
    // Handle legacy string values
    if (value === 'All' || !value) {
      return allStatusValues;
    }
    return [value];
  };

  const selectedStatuses = getDisplayValue();
  const isAllSelected =
    selectedStatuses.length === allStatusValues.length &&
    allStatusValues.every((status) => selectedStatuses.includes(status));

  const handleChange = (event, child) => {
    const { value: newValue } = event.target;

    // If user clicked the "All" menu item
    if (child?.props?.value === 'All') {
      if (isAllSelected) {
        // If all are selected, deselect all (empty array)
        onChange([]);
      } else {
        // If not all are selected, select all
        onChange([...allStatusValues]);
      }
      return;
    }

    // Normal multi-select changes
    onChange(newValue);
  };

  // Render selected value display
  const renderValue = (selected) => {
    // Check if it's "all" (empty array or all statuses selected both mean show all items)
    if (
      !selected ||
      selected.length === 0 ||
      selected.length === allStatusValues.length
    ) {
      return t('All');
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return `${selected[0]} (+ ${selected.length - 1})`;
  };

  return (
    <FormControl size="small" sx={{ minWidth: 232 }}>
      <Box>
        <InputLabel id="status-filter-label">{t('Status')}</InputLabel>
        <SearchableSelect
          labelId="status-filter-label"
          id="status-filter"
          multiple
          value={selectedStatuses}
          label={t('Status')}
          onChange={handleChange}
          disabled={disabled}
          MenuProps={MenuProps}
          searchThreshold={0}
          searchPlaceholder={t('Search') || 'Search...'}
          sx={{ pl: 1, width: '100%' }}
          input={
            <OutlinedInput id="select-status-filter" label={t('Status')} />
          }
          renderValue={renderValue}
        >
          {/* "All" menu item */}
          <MenuItem value="All">
            <Checkbox
              checked={selectedStatuses.length === allStatusValues.length}
            />
            <ListItemText primary={t('All')} />
          </MenuItem>

          {/* Individual status items */}
          {allStatusValues.map((statusValue) => (
            <MenuItem key={statusValue} value={statusValue}>
              <Checkbox checked={selectedStatuses.includes(statusValue)} />
              <ListItemText primary={statusValue} />
            </MenuItem>
          ))}
        </SearchableSelect>
      </Box>
    </FormControl>
  );
};

export default StatusFilter;
